import { useState, useEffect, useRef } from 'react';
import { Check, ExternalLink, Gem } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Button } from '../components/ui/button';
import EhAuthDialog from '../components/EhAuthDialog';

import { EhPaymentSuccessDialog } from '../components/EhPaymentSuccessDialog';
import { EhPaymentErrorDialog } from '../components/EhPaymentErrorDialog';
import { GEM_PACKAGES, generateOrderId, getGemStripeConfig, STRIPE_PAYEMENT_BASE_URL, GemPackage } from '../lib/ehPrices';
import { cn } from '../lib/utils';
import { loadAtlosScript, checkPayment, getPaymentEmail } from '../lib/ehAtlosPayment';
import { SEO } from '../components/SEO';
import { useTracking, PAYMENT_EVENTS } from '../hooks/useTracking';
import { useUserInfo } from '@/hooks/useUserInfo';
import { DEFAULT_BALANCE, useGemBalance } from '@/hooks/useGemBalance';
import { EhEndorselyScript } from '@/components/EhEndorselyScript';
import { isFromAppStores, isHidingMonetizationInStores, isTwaOrPlayStoreInstalled } from '@/lib/detection';
import { usePlayBilling } from '@/hooks/usePlayBilling';
import { useAppleStoreKit, SwiftPurchaseState } from '@/hooks/useAppleStoreKit';

export default function GemsPage() {
  const { trackEvent } = useTracking();
  const { isRenewableSubscriber, isSubscriber, isUserDetailsLoading, user, refetchUserInfo } = useUserInfo();
  const { balance, getRefreshedBalance, refreshBalance } = useGemBalance();
  const { 
    isSupported: isPlayBillingSupported,
    getProductDetails: getPlayProductDetails,
    purchase: purchaseWithPlayBilling
  } = usePlayBilling();
  const { 
    isSupported: isAppleStoreKitSupported, 
    products: appleStoreProducts,
    getProductDetails: getAppleProductDetails,
    purchase: purchaseWithAppleStoreKit 
  } = useAppleStoreKit();

  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [paymentCheckStatus, setPaymentCheckStatus] = useState<'' | 'pending' | 'verified' | 'failed'>('');
  const [selectedPackage, setSelectedPackage] = useState<GemPackage | null>(null);
  const [currentPaymentMethod, setCurrentPaymentMethod] = useState<'crypto' | 'card' | 'apple_store' | 'play_billing' | null>(null);
  const [isPlayStoreInstalled, setIsPlayStoreInstalled] = useState(false);
  const [isRestoringApplePurchase, setIsRestoringApplePurchase] = useState<boolean | null>(null);
  const [playStorePrices, setPlayStorePrices] = useState<Record<string, string>>({});

  const currentOrderIdRef = useRef<string | null>(null);
  const atlosRetries = useRef(0);
  const [isAwaitingCardPayment, setIsAwaitingCardPayment] = useState(false);
  const cardPaymentTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isComponentMountedRef = useRef(true);
  const initialBalanceRef = useRef<number>(0);

  // Init
  useEffect(() => {
    if (!isUserDetailsLoading) {
      setIsLoading(false);
    }

    const checkPlayStoreInstalled = async () => {
      setIsPlayStoreInstalled(await isTwaOrPlayStoreInstalled());
    }
    if (isPlayBillingSupported) {
      void checkPlayStoreInstalled();
    }
  }, [isPlayBillingSupported, isUserDetailsLoading]);
  
  // Refresh balance and load Atlos script on load
  useEffect(() => {
    if (!isLoading && user) {
      void refreshBalance();
      void loadAtlosScript();
    }
  }, [isLoading, refreshBalance, user]);

  // Fetch Apple App Store product details for gem packages
  useEffect(() => {
    const fetchAppleProductDetails = async () => {
      if (isAppleStoreKitSupported) {
        // Get all gem package SKUs that have appleStoreSku
        const gemSkus = GEM_PACKAGES
          .filter(pkg => pkg.appleStoreSku)
          .map(pkg => pkg.appleStoreSku!);
        
        if (gemSkus.length > 0) {
          await getAppleProductDetails(gemSkus);
        }
      }
    };
    fetchAppleProductDetails();
  }, [isAppleStoreKitSupported, getAppleProductDetails]);
  
  // Fetch Google Play product details for gem packages
  useEffect(() => {
    const fetchPlayProductDetails = async () => {
      if (isPlayBillingSupported) {
        // Get all gem package SKUs that have googlePlaySku
        const gemSkus = GEM_PACKAGES
          .filter(pkg => pkg.googlePlaySku)
          .map(pkg => pkg.googlePlaySku!);
        if (gemSkus.length > 0) {
          const details = await getPlayProductDetails(gemSkus);
          if (details && details.length > 0) {
            const map: Record<string, string> = {};
            for (const d of details) {
              const localized = new Intl.NumberFormat(
                navigator.language,
                { style: 'currency', currency: d.price.currency }
              ).format(d.price.value);
              map[d.itemId] = localized;
            }
            setPlayStorePrices(map);
          }
        }
      }
    };
    fetchPlayProductDetails();
  }, [isPlayBillingSupported, getPlayProductDetails]);
  
  // Set initial selected package
  useEffect(() => {
    if (GEM_PACKAGES.length > 0 && !selectedPackage) {
      // Default to the medium package if available
      const defaultPackage = GEM_PACKAGES.find(p => p.id === 'm') || GEM_PACKAGES[0];
      setSelectedPackage(defaultPackage);
    }
  }, [selectedPackage]);

  // Handle automatic gem balance refresh when user returns from card payment
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!document.hidden && isAwaitingCardPayment && isComponentMountedRef.current) {
        if (import.meta.env.DEV) {
          console.log('[DEV] Tab became visible, refreshing user balance after card payment attempt');
        }
        try {
          await new Promise(resolve => setTimeout(resolve, 10000)); // wait for 10 seconds for stripe webhook
          const newBalance = await getRefreshedBalance();
          
          // Check if balance increased (indicating successful payment)
          if (newBalance && newBalance.total > initialBalanceRef.current) {
            if (import.meta.env.DEV) {
              console.log('[DEV] Payment detected: balance increased from', initialBalanceRef.current, 'to', newBalance.total);
            }
            setIsAwaitingCardPayment(false);
            // if ever user initiates a new payment
            initialBalanceRef.current = newBalance.total;

            trackEvent(PAYMENT_EVENTS.PAYMENT_COMPLETED, {
              paymentMethod: 'card',
              gemPackage: selectedPackage,
              billedAmount: selectedPackage?.price,
              isGemPurchase: true
            });
          }
        } catch (error) {
          if (import.meta.env.DEV) console.error('[DEV] Error refreshing user after card payment:', error);
        }
      }
    };

    // const handleWindowFocus = async () => {
    //   if (isAwaitingCardPayment && isComponentMountedRef.current) {
    //     if (import.meta.env.DEV) {
    //       console.log('[DEV] Window focused, refreshing user balance after card payment attempt');
    //     }
    //     try {
    //       const newBalance = await getRefreshedBalance();
    //       
    //       // Check if balance increased (indicating successful payment)
    //       if (newBalance.total > initialBalanceRef.current) {
    //         if (import.meta.env.DEV) {
    //           console.log('[DEV] Payment detected: balance increased from', initialBalanceRef.current, 'to', newBalance.total);
    //         }
    //         setIsAwaitingCardPayment(false);
    //       }
    //     } catch (error) {
    //       if (import.meta.env.DEV) console.error('[DEV] Error refreshing buser after card payment:', error);
    //     }
    //   }
    // };

    if (isAwaitingCardPayment) {
      document?.addEventListener('visibilitychange', handleVisibilityChange);
      // window?.addEventListener('focus', handleWindowFocus);

      // Set timeout to stop waiting after 60 minutes
      const timeout = setTimeout(() => {
        if (isComponentMountedRef.current) {
          setIsAwaitingCardPayment(false);
        }
      }, 60 * 60 * 1000);

      cardPaymentTimeoutRef.current = timeout;

      return () => {
        document?.removeEventListener('visibilitychange', handleVisibilityChange);
        // window?.removeEventListener('focus', handleWindowFocus);
        if (cardPaymentTimeoutRef.current) {
          clearTimeout(cardPaymentTimeoutRef.current);
          cardPaymentTimeoutRef.current = null;
        }
      };
    }
  }, [isAwaitingCardPayment, balance.total, getRefreshedBalance, selectedPackage, trackEvent]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isComponentMountedRef.current = false;
      if (cardPaymentTimeoutRef.current) {
        clearTimeout(cardPaymentTimeoutRef.current);
      }
    };
  }, []);

  const handleAuthDialogOpenChange = (open: boolean) => {
    setShowAuthDialog(open);
  };

  const handlePackageSelect = (gemPackage: GemPackage) => {
    // automatically proceed to payment if gem package is already selected
    if (gemPackage.id === selectedPackage?.id ) {
      if (isAppleStoreKitSupported) {
        handleAppleGemPackagePurchase(gemPackage);
      } else if (isPlayBillingSupported) {
        void handlePlayGemPackagePurchase(gemPackage);
      } else {
        handleCCPayment();
      }
      return;
    }

    setSelectedPackage(gemPackage);
  };

  const handlePlayGemPackagePurchase = async (gemPackage: GemPackage) => {
    if (!user) {
      setShowAuthDialog(true);
      return;
    }
    if (!gemPackage.googlePlaySku) {
      setErrorMessage('This gem package is not available on Google Play.');
      setShowErrorDialog(true);
      return;
    }

    setCurrentPaymentMethod('play_billing');
    setIsProcessing(true);
    try {
      const { success } = await purchaseWithPlayBilling({ sku: gemPackage.googlePlaySku, userId: user.id });
      if (success) {
        setShowSuccessDialog(true);
        await refreshBalance();
      }
    } catch (error) {
      setErrorMessage('Sorry, an error happened. Please try again later.');
      setShowErrorDialog(true);
      trackEvent(PAYMENT_EVENTS.PAYMENT_ERROR, {
        paymentMethod: 'googlePlay',
        error: `GemsPage.handlePlayGemPackagePurchase failed: ${error instanceof Error ? error.message : String(error)}`,
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleAppleGemPackagePurchase = async (gemPackage: GemPackage) => {
    if (!user) {
      setShowAuthDialog(true);
      return;
    }

    if (!gemPackage.appleStoreSku) {
      setErrorMessage('This gem package is not available on Apple App Store.');
      setShowErrorDialog(true);
      return;
    }

    const appleErrorMessage = 'Sorry, an error happened. Please try again later, our team is working on it.';

    // Find the corresponding Apple product for formatted pricing
    const appleProduct = appleStoreProducts?.find(product => 
      product.attributes?.offerName === gemPackage.appleStoreSku
    );
    
    if (!appleProduct?.attributes?.offerName) {
      setErrorMessage(appleErrorMessage);
      setShowErrorDialog(true);
      return;
    }

    setCurrentPaymentMethod('apple_store');
    setIsProcessing(true);


    const onResult = (status: SwiftPurchaseState) => {
      if (['failed', 'notFound'].includes(status)) {
        setErrorMessage(appleErrorMessage);
        setShowErrorDialog(true);
        return;
      }

      // status === 'success'
      // The actual gem purchase will be processed via Apple Server Notifications.
      setShowErrorDialog(false);
      
      // Refresh user info and balance after a delay
      setTimeout(async () => {
        const newBalance = await getRefreshedBalance();

        // retry if balance didn't increase
        if (newBalance && newBalance.total <= initialBalanceRef.current) {
          setTimeout(async () => {
            await refreshBalance();
          }, 10000);
        }
      }, 5000);
    };

    try {
      await purchaseWithAppleStoreKit({
        onResult,
        sku: gemPackage.appleStoreSku,
        userId: user.id
      });
    } catch (error) {
      setErrorMessage(appleErrorMessage);
      setShowErrorDialog(true);
      trackEvent(PAYMENT_EVENTS.PAYMENT_ERROR, {
        paymentMethod: 'appleStore',
        error: `GemsPage.handleAppleGemPackagePurchase failed: ${error instanceof Error ? error.message : String(error)}`,
      });
    } finally {
      setIsProcessing(false);
    }
  };
  
  const handleCryptoPayment = () => {
    if (!selectedPackage) {
      trackEvent(PAYMENT_EVENTS.PAYMENT_CLICKED, {
        paymentMethod: 'crypto',
        noSelectedPlan: true,
        isGemPurchase: true
      });
      return;
    }
    
    // If user is not logged in, show auth dialog
    if (!user) {
      setShowAuthDialog(true);
      return;
    }
    
    setIsProcessing(true);
    setCurrentPaymentMethod('crypto');

    // Generate a unique order ID
    const orderId = generateOrderId();

    trackEvent(PAYMENT_EVENTS.PAYMENT_CLICKED, {
      paymentMethod: 'crypto',
      gemPackage: selectedPackage,
      billedmount: selectedPackage.price,
      orderId: !window.atlos ? 'pending' : orderId,
      isGemPurchase: true
    });
    
    if (!window.atlos) {
      if (atlosRetries.current > 5) {
        const errorMessage = 'Payment system is not loaded. Please try again or refresh the page.';
        setErrorMessage(errorMessage);
        setIsProcessing(false);
        setShowErrorDialog(true);
        trackEvent(PAYMENT_EVENTS.PAYMENT_ERROR, {
          paymentMethod: 'crypto',
          gemPackage: selectedPackage,
          billedAmount: selectedPackage.price,
          orderId: orderId,
          isGemPurchase: true,
          error: `Failed to load atl payment library: '${errorMessage}'`
        });
        return;
      }
      void loadAtlosScript();
      setTimeout(() => {
        atlosRetries.current++;
        handleCryptoPayment();
      }, 3000);
      return;
    }

    currentOrderIdRef.current = orderId;
    
    // Handle Atlos payment
    window.atlos.Pay({
      merchantId: import.meta.env.VITE_ATL_MERCHANT_ID as string,
      orderId: orderId,
      orderAmount: selectedPackage.price,
      orderCurrency: 'USD',
      userEmail: getPaymentEmail(user.email || ''),
      onSuccess: (data) => handleCryptoPaymentSuccess(selectedPackage, orderId, data),
      onCanceled: () => handlePaymentCanceled(),
      onCompleted: () => handlePaymentCompleted(),
      theme: 'dark'
    });
  };
  
  const handleCryptoPaymentSuccess = async (gemPackage: GemPackage, orderId: string, data: string) => {
    if (import.meta.env.DEV) {
      console.log('[DEV] Payment success:', data);
    }
    
    try {
      setPaymentCheckStatus('pending');
      await checkPayment(gemPackage, orderId, user?.id);
      setPaymentCheckStatus('verified');
      
      // Track payment completed event
      trackEvent(PAYMENT_EVENTS.PAYMENT_COMPLETED, {
        paymentMethod: 'crypto',
        gemPackage,
        billedAmount: gemPackage.price,
        orderId: orderId,
        isGemPurchase: true
      });

      await refreshBalance();
    } catch (error) {
      setPaymentCheckStatus('failed');

      trackEvent(PAYMENT_EVENTS.PAYMENT_ERROR, {
        paymentMethod: 'crypto',
        gemPackage,
        billedAmount: gemPackage.price,
        orderId: orderId,
        isGemPurchase: true,
        error: error instanceof Error ? error.message : String(error)
      });
    } finally {
      setIsProcessing(false);
      setShowSuccessDialog(true);
    }
  };
  
  const handlePaymentCanceled = () => {
    if (import.meta.env.DEV) {
      console.log('[DEV] Payment canceled');
    }

    if (selectedPackage && currentOrderIdRef.current) {
      trackEvent(PAYMENT_EVENTS.PAYMENT_CANCELED, {
        paymentMethod: 'crypto',
        gemPackage: selectedPackage,
        billedAmount: selectedPackage.price,
        orderId: currentOrderIdRef.current,
        isGemPurchase: true,
      });
    }
    
    setIsProcessing(false);
  };
  
  const handlePaymentCompleted = () => {
    setIsProcessing(false);
  };
  
  const retryPaymentCheck = async () => {
    if (!selectedPackage || !currentOrderIdRef.current || !user?.id) return;
    
    try {
      setPaymentCheckStatus('pending');
      await checkPayment(selectedPackage, currentOrderIdRef.current, user.id);
      setPaymentCheckStatus('verified');
      
      // Track payment completed on retry
      trackEvent(PAYMENT_EVENTS.PAYMENT_COMPLETED, {
        paymentMethod: 'crypto',
        gemPackage: selectedPackage,
        billedAmount: selectedPackage.price,
        orderId: currentOrderIdRef.current,
        isGemPurchase: true,
        isRetry: true
      });
      
      await refreshBalance();
    } catch (error) {
      if (import.meta.env.DEV) console.error('[DEV] Payment verification retry failed:', error);
      setPaymentCheckStatus('failed');
      setErrorMessage('Payment verification failed.');

      trackEvent(PAYMENT_EVENTS.PAYMENT_ERROR, {
        paymentMethod: 'crypto',
        gemPackage: selectedPackage,
        billedAmount: selectedPackage.price,
        orderId: currentOrderIdRef.current,
        error: error instanceof Error ? error.message : String(error),
        isGemPurchase: true,
        isRetry: true,
      });
      
      setShowSuccessDialog(false);
      setShowErrorDialog(true);
    }
  };

  const buildCCPaymentUrl = (gemPackage: GemPackage) => {
    const stripeConfig = getGemStripeConfig(gemPackage);
    let paymentUrl = `${STRIPE_PAYEMENT_BASE_URL}${stripeConfig.paymentLink}?client_reference_id=${user?.id}`;
  
    // Add prefilled email in production
    if (user?.email && !import.meta.env.DEV) {
      paymentUrl = `${paymentUrl}&prefilled_email=${encodeURIComponent(user.email)}`;
    }
    return paymentUrl;
  };

  const handleCCPayment = () => {
    if (!selectedPackage) {
      trackEvent(PAYMENT_EVENTS.PAYMENT_CLICKED, {
        paymentMethod: 'card',
        noSelectedPlan: true,
        isGemPurchase: true
      });
      return;
    }

    // If user is not logged in, show auth dialog
    if (!user) {
      setShowAuthDialog(true);
      return;
    }

    try {
      setCurrentPaymentMethod('card');

      // Save current balance before payment
      initialBalanceRef.current = balance.total;

      // Create payment link with user ID as client reference
      const paymentUrl = buildCCPaymentUrl(selectedPackage);

      // Open payment link in new tab
      window.open(paymentUrl, '_blank', 'noopener,noreferrer');

      // Track payment attempt
      trackEvent(PAYMENT_EVENTS.PAYMENT_CLICKED, {
        paymentMethod: 'card',
        gemPackage: selectedPackage,
        billedAmount: selectedPackage.price,
        isGemPurchase: true
      });
      
      // Start monitoring for user return to refresh balance
      setIsAwaitingCardPayment(true);

    } catch (error) {
      setErrorMessage('Failed to initiate payment. Please try again.');
      setShowErrorDialog(true);

      trackEvent(PAYMENT_EVENTS.PAYMENT_ERROR, {
        paymentMethod: 'card',
        gemPackage: selectedPackage,
        billedAmount: selectedPackage.price,
        isGemPurchase: true,
        error: error instanceof Error ? error.message : String(error)
      });
    }
  };
  
  if (isLoading || (isAppleStoreKitSupported && appleStoreProducts.length === 0)) {
    return (
      <div className="flex-auto flex flex-col">
        <div className="flex items-center justify-center flex-1">
          <div className="animate-spin size-8 border-4 border-white/20 border-t-white rounded-full" />
        </div>
      </div>
    );
  }

  const hidePaymentsOnRetrictedPlatform = isHidingMonetizationInStores()
    || (isPlayStoreInstalled && isPlayBillingSupported);
  const showInAppPayments = isPlayBillingSupported || isAppleStoreKitSupported;
  
  return (
    <div className="flex-auto bg-zinc-800 text-white">
      <SEO 
        title="Buy Gems - EveningHoney.AI"
        description="Purchase gems to unlock premium features like AI girlfriend creation, image generation, and voice messages. Choose from various gem packages with bonus rewards."
        ogUrl="/gems"
      />
      <EhEndorselyScript />
      
      <div className="flex-1 container max-w-6xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Buy Gems</h1>
          {balance.total > DEFAULT_BALANCE.total && (
            <p className="text-zinc-300">
              {user ? `Current balance: ${balance.total} 💎` : 'Purchase gems to unlock premium features'}
            </p>
          )}
        </div>
        
        {/* Main content container with gradient border */}
        <div className="rounded-lg px-4 md:px-8 pt-4 md:pt-8 bg-gradient-to-br from-zinc-800/70 to-zinc-950/70 border border-zinc-800/80 shadow-lg">
          <div className="grid md:grid-cols-12 gap-6">
            {/* Left Column - Marketing */}
            <div className="md:col-span-3">
              <div className="rounded-lg overflow-hidden h-full flex flex-col">
                <div className="text-center mb-3">
                  <h2 className="text-2xl font-bold text-pink-600">
                    <span>Get An Exclusive</span>
                    <br />
                    <span>Package Discount</span>
                  </h2>
                  <p className="text-zinc-200 mt-2">
                    {isFromAppStores() ? (
                      <span>Enjoy more content anytime!</span>
                    ) : (
                      <span>Enjoy hot content anytime!</span>
                    )}
                    <span> Purchased gems don't expire.</span>
                  </p>
                </div>
                
                <div className="mt-auto">
                  <img 
                    src="/images/eh_char_dress-bl.webp" 
                    alt="AI Girlfriend"
                    className="w-full h-auto hidden md:block"
                  />
                </div>
              </div>
            </div>
            
            {/* Center Column - Gem Packages */}
            {hidePaymentsOnRetrictedPlatform && !showInAppPayments ? (
              <div className="md:col-span-6 flex items-center justify-center">
                <div className="text-center text-2xl mb-3 px-8">
                  <h2 className="text-white mb-2">Purchase gems with a discount on our website.</h2>
                  {/* TODO: Add link back to website using Intent to open in browser */}
                  {/* <a
                    href="https://www.eveninghoney.ai/?utm_medium=android-app&utm_source=gem"
                    className="font-bold text-pink-600 hover:text-pink-500 underline"
                  >www.eveninghoney.ai</a> */}
                  {/* <div className="font-bold text-pink-600">www.eveninghoney.ai</div> */}
                </div>
              </div>
            ) : (
            <div className="md:col-span-6">
              {/* Gem Packages Grid */}
              <div className="grid grid-cols-2 gap-4">
                {GEM_PACKAGES
                .filter(pkg => !isAppleStoreKitSupported || pkg.appleStoreSku)
                .map((gemPackage) => {
                  const isSelected = selectedPackage?.id === gemPackage.id;
                  
                  return (
                    <div 
                      key={gemPackage.id}
                      className={cn(
                        'relative rounded-lg cursor-pointer transition-all duration-200 border-2',
                        isSelected ? 'bg-gradient-to-r from-zinc-800/90 to-zinc-900/90 border-rose-700/50' : 
                        'bg-zinc-900/60 border-zinc-800/50 hover:border-zinc-700/50',
                      )}
                      onClick={() => handlePackageSelect(gemPackage)}
                    >
                      <div className="p-4 relative">
                        {gemPackage.bonus && (
                          <div className="absolute -top-2 right-2 inline-flex items-center mx-1 px-2 py-1 rounded-lg text-xs font-bold bg-pink-700 text-white">
                            {`+ ${Math.round(gemPackage.bonus * 100)}%`}
                          </div>
                        )}
                        
                        <div className="flex flex-col items-center text-center">
                          <div className="flex items-center justify-center mb-2">
                            {/* <Gem className="h-6 w-6 text-amber-400 mr-1" /> */}
                            <span className="text-2xl font-bold text-white">{`💎 ${gemPackage.gems}`}</span>
                          </div>
                          
                          <div className="text-2xl font-bold text-white mb-1">
                            {(() => {
                              if (isPlayBillingSupported && gemPackage.googlePlaySku && playStorePrices[gemPackage.googlePlaySku]) {
                                return playStorePrices[gemPackage.googlePlaySku];
                              }
                              const appleProduct = appleStoreProducts?.find(p => p.attributes?.offerName === gemPackage.appleStoreSku);
                              return appleProduct?.attributes?.offers[0]?.priceFormatted || `$${gemPackage.price}`;
                            })()}
                          </div>
                          
                          {gemPackage.gemsWithoutBonus && gemPackage.bonus && (
                            <div className="text-xs text-zinc-400">
                              <span>{gemPackage.gemsWithoutBonus} + {Math.round(gemPackage.gemsWithoutBonus * gemPackage.bonus)} bonus</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
                
              {/* Payment Buttons */}
              <div className="mt-6">
                <Button 
                  className={cn(
                    'w-full py-6 bg-slate-700 hover:bg-slate-600 border border-slate-700',
                    (isAppleStoreKitSupported || isPlayBillingSupported) && 'hidden'
                  )}
                  onClick={handleCryptoPayment}
                  disabled={isProcessing}
                >
                  {(isProcessing && currentPaymentMethod === 'crypto') ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin mr-2 size-5 border-2 border-white/30 border-t-white rounded-full" />
                      <div>Processing...</div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <div className="font-bold">Pay with crypto</div>
                      <div className="flex items-center space-x-1">
                        <img src="/logos/btc-logo.svg" alt="Bitcoin" className="h-5 w-5" />
                        <img src="/logos/eth-logo.svg" alt="Ethereum" className="h-5 w-5" />
                        <img src="/logos/ltc-logo.svg" alt="Litecoin" className="h-5 w-5" />
                        <img src="/logos/xmr-logo.svg" alt="Monero" className="h-5 w-5" />
                      </div>
                    </div>
                  )}
                </Button>

                {/* Credit Card Payment Button */}
                <Button
                  className="w-full py-6 mt-2 bg-pink-600 hover:bg-pink-500 flex items-center justify-center gap-2 border border-pink-600"
                  onClick={
                    isAppleStoreKitSupported
                      ? () => selectedPackage && handleAppleGemPackagePurchase(selectedPackage)
                      : isPlayBillingSupported
                        ? () => selectedPackage && handlePlayGemPackagePurchase(selectedPackage)
                        : handleCCPayment
                  }
                  disabled={isProcessing || !selectedPackage}
                  type="button"
                >
                  <div className="flex flex-wrap items-center justify-center gap-1">
                    {isProcessing && ['card', 'apple_store', 'play_billing'].includes(currentPaymentMethod || '') && (
                      <div className="animate-spin h-5 w-5 border-2 border-pink-400 border-t-transparent rounded-full" />
                    )}
                    <ExternalLink className={`h-4 w-4${(isAppleStoreKitSupported || isPlayBillingSupported) ? ' hidden' : ''}`} />
                    <span className="font-bold">{`Purchase ${selectedPackage?.gems} Gems`}</span>
                    {!isAppleStoreKitSupported && !isPlayBillingSupported && (
                      <>
                        <img src="/logos/visa-logo-white.svg" alt="Visa" className="h-6" />
                        <img src="/logos/mastercard-logo.svg" alt="Mastercard" className="h-6" />
                        <img src="/logos/apple-pay-logo.svg" alt="Apple Pay" className="h-4" />
                      </>
                    )}
                    {isPlayBillingSupported && (
                      <img src="/logos/google-logo.svg" alt="Google Play" className="h-6" />
                    )}
                  </div>
                </Button>

                {isAppleStoreKitSupported && (
                  <div className="flex items-center justify-center mt-2">
                    <Button 
                      className={cn(
                        'py-4 bg-slate-700 hover:bg-slate-600 border border-slate-700',
                        isRestoringApplePurchase === false ? 'hidden' : ''
                      )}
                      onClick={() => {
                        setIsRestoringApplePurchase(true);
                        refetchUserInfo().then(() => {
                          setIsRestoringApplePurchase(false);
                        });
                      }}
                      disabled={isRestoringApplePurchase === true}
                    >
                      {(isRestoringApplePurchase) ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin mr-2 size-5 border-2 border-white/30 border-t-white rounded-full" />
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-2">
                          Restore purchase
                        </div>
                      )}
                    </Button>
                  </div>
                )}
                
                {isAwaitingCardPayment && (
                  <div className="text-center mt-2 text-sm text-zinc-400">
                    <div>
                      <span className="inline-block animate-spin h-3 w-3 border-2 border-pink-400 border-t-transparent rounded-full mr-2" />
                      <span>Complete your payment in the opened tab, then return here.</span>
                    </div>
                    <div>Your gem balance will automatically refresh when you return.</div>
                  </div>
                )}
                
                {!isAppleStoreKitSupported && (
                  <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center mt-3 mb-1 sm:mb-6 gap-2">
                  <div className="flex items-center text-green-400 text-sm">
                    <Check className="h-4 w-4 mr-1" />
                    <span>Billed as </span>
                    <strong className="mx-1">“EH”</strong>
                    <span> on your bank statement</span>
                  </div>
                  {/* <span className="mx-2 text-zinc-500">•</span> */}
                  <div className="flex items-center text-green-400 text-sm">
                    <Check className="h-4 w-4 mr-1" />
                    <span>No hidden fees</span>
                  </div>
                </div>
                )}
              </div>
            </div>
            )}
            
            {/* Right Column - Benefits */}
            <div className="md:col-span-3">
              <div className="rounded-lg overflow-hidden h-full flex flex-col">
                <div>
                  <h2 className="text-xl font-bold text-white mb-4">Gem Benefits</h2>
                  <ul className="space-y-3">
                    <li className="flex flex-col">
                      <span className="flex items-start">
                        <Gem className="h-5 w-5 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-zinc-300">Chat Images 💎</span>
                        <span className="text-sm text-zinc-300">&nbsp;{isSubscriber ? 'FREE' : '2'}</span>
                      </span>
                      {isSubscriber ? (
                        <span className="ml-7 text-xs text-zinc-400">Unlimited in all subscriptions!</span>                      
                      ) : (
                        <Link to="/subscription" className="ml-7 text-xs text-amber-500 hover:underline">
                          Subscribe for unlimited images
                        </Link>
                      )}
                    </li>
                    <li className="flex items-start">
                      <Gem className="h-5 w-5 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-zinc-300">Voice messages 💎 0.2</span>
                    </li>
                    <li className="flex items-start">
                      <Gem className="h-5 w-5 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-zinc-300">Live Phone calls 💎 3 / min.</span>
                    </li>
                    <li className="flex items-start">
                      <Gem className="h-5 w-5 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-zinc-300">
                        {!isFromAppStores() && (
                          <span>NSFW </span>
                        )}
                        <span>Photo Studio 💎 2</span>
                      </span>
                    </li>
                    <li className="flex items-start">
                      <Gem className="h-5 w-5 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-zinc-300">Create AI Companions 💎 10</span>
                    </li>
                    <li className="flex flex-col">
                      <span className="flex items-start">
                        <Gem className="h-5 w-5 text-amber-400 mr-2 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-zinc-300">Chat & Studio Videos 💎 10</span>
                      </span>
                      {!isRenewableSubscriber && (
                        <Link
                          to="/subscription"
                          className="text-amber-500 ml-7 text-xs hover:underline"
                        >
                          Unlimited Chat videos available!
                        </Link>   
                      )}
                    </li>
                  </ul>
                </div>
                
                <div className="mt-auto">
                  <img 
                    src="/images/eh_char_bikini-anime.webp" 
                    alt="AI Girlfriend"
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Auth Dialog */}
      <EhAuthDialog
        open={showAuthDialog}
        onOpenChange={handleAuthDialogOpenChange}
      />
      
      {/* Success Dialog */}
      <EhPaymentSuccessDialog
        open={showSuccessDialog}
        onOpenChange={setShowSuccessDialog}
        purchaseOption={selectedPackage}
        paymentCheckStatus={paymentCheckStatus}
        onRetryVerification={currentPaymentMethod === 'crypto' ? retryPaymentCheck : undefined}
      />
      
      {/* Error Dialog */}
      <EhPaymentErrorDialog
        open={showErrorDialog}
        onOpenChange={setShowErrorDialog}
        error={errorMessage}
        onRetry={currentPaymentMethod === 'crypto'
          ? handleCryptoPayment
          : isAppleStoreKitSupported
            ? undefined
            : handleCCPayment}
      />
    </div>
  );
} 